import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ExternalLink, Play, Pause, Maximize2, X, Globe, Video, Volume2, VolumeX, Instagram } from 'lucide-react';

const OurWorkPage = () => {
  const { t } = useLanguage();
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const videoRefs = useRef({});
  const modalVideoRef = useRef(null);
  const [modalMuted, setModalMuted] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  // Website data - actual client websites
  const websites = [
    {
      id: 1,
      name: t('ourWork.website1Name') || 'New Life in Florida',
      description: t('ourWork.website1Desc') || 'Real estate and relocation services website with modern design.',
      url: 'https://newlifeinflorida.com',
      tags: ['Real Estate', 'Responsive', 'Modern']
    },
    {
      id: 2,
      name: t('ourWork.website2Name') || 'Psikolog Eda Arslan',
      description: t('ourWork.website2Desc') || 'Professional psychology website with elegant design.',
      url: 'https://pskedaarslan.com',
      tags: ['Psychology', 'Professional', 'Elegant']
    }
  ];

  // Videos data - horizontal videos (Fashion, Bag, TPack removed)
  const horizontalVideos = [
    {
      id: 1,
      title: t('ourWork.video5Title') || 'The Meltque Advertisement',
      description: t('ourWork.video5Desc') || 'Restaurant promotional video.',
      src: 'https://customer-assets.emergentagent.com/job_pirone-website/artifacts/ai8iodr6_the%20meltque%20reklam.mp4',
      category: 'restaurant',
    },
    {
      id: 2,
      title: t('ourWork.video2Title') || 'Dan Advertisement',
      description: t('ourWork.video2Desc') || 'Creative brand promotion video.',
      src: 'https://customer-assets.emergentagent.com/job_pirone-website/artifacts/tn8rgxea_dan%20reklam.mp4',
      category: 'restaurant',
    },
    {
      id: 3,
      title: t('ourWork.video3Title') || 'Enzo Video Advertisement',
      description: t('ourWork.video3Desc') || 'Dynamic promotional content.',
      src: 'https://customer-assets.emergentagent.com/job_pirone-website/artifacts/kplai76x_enzo%20video%20reklam.mp4',
      category: 'restaurant',
    }
  ];

  // Vertical videos (Reels/TikTok style) — Soli, Soli2 removed
  const verticalVideos = [
    {
      id: 7,
      title: t('ourWork.video7Title') || 'Macarina Advertisement',
      description: t('ourWork.video7Desc') || 'Brand promotion video.',
      src: 'https://customer-assets.emergentagent.com/job_pirone-website/artifacts/2vmhtqkx_macarina%20reklam.mp4',
      category: 'cafe',
    },
    {
      id: 8,
      title: t('ourWork.video8Title') || 'Rocs Advertisement',
      description: t('ourWork.video8Desc') || 'Creative brand video.',
      src: 'https://customer-assets.emergentagent.com/job_pirone-website/artifacts/c7b6k5jk_rocs%20reklam.mp4',
      category: 'retail',
    },
    {
      id: 10,
      title: t('ourWork.video10Title') || 'Mama Advertisement',
      description: t('ourWork.video10Desc') || 'Creative promotional video.',
      src: 'https://customer-assets.emergentagent.com/job_pirone-website/artifacts/jzghcinh_mama%20reklam.mp4',
      category: 'restaurant',
    }
  ];

  // Pause all inline videos (used when opening modal / leaving page)
  const pauseAllInlineVideos = () => {
    Object.values(videoRefs.current).forEach(v => {
      if (v) {
        try { v.pause(); } catch (err) { console.debug('Video pause skipped:', err?.message); }
      }
    });
    setPlayingVideo(null);
  };

  const handlePlayVideo = (videoId) => {
    const videoEl = videoRefs.current[videoId];
    if (!videoEl) return;

    if (playingVideo === videoId) {
      videoEl.pause();
      setPlayingVideo(null);
    } else {
      // Pause any currently playing video first
      pauseAllInlineVideos();
      videoEl.play().catch(() => { /* autoplay blocked */ });
      setPlayingVideo(videoId);
    }
  };

  // Open modal: ALWAYS pause inline videos so only the modal video plays
  const openModal = (video) => {
    pauseAllInlineVideos();
    setSelectedVideo(video);
  };

  const closeModal = () => {
    if (modalVideoRef.current) {
      try { modalVideoRef.current.pause(); } catch (err) { console.debug('Modal video pause skipped:', err?.message); }
    }
    setSelectedVideo(null);
  };

  // Close modal on ESC + lock body scroll
  useEffect(() => {
    if (!selectedVideo) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [selectedVideo]);

  // Reset mute state when opening a new video
  useEffect(() => {
    setModalMuted(false);
  }, [selectedVideo?.id]);

  const toggleModalMute = () => {
    if (!modalVideoRef.current) return;
    const next = !modalMuted;
    modalVideoRef.current.muted = next;
    setModalMuted(next);
  };

  return (
    <div className="min-h-screen bg-transparent relative z-10">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        {/* Background Glow Effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1400px] h-[800px]"
            style={{ background: 'radial-gradient(ellipse at center top, rgba(139, 92, 246, 0.1) 0%, transparent 60%)' }}
          />
          <div className="absolute top-1/3 right-0 w-[600px] h-[600px]"
            style={{ background: 'radial-gradient(circle at center, rgba(56, 189, 248, 0.05) 0%, transparent 60%)' }}
          />
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium mb-6">
              <Globe className="w-4 h-4" />
              {t('ourWork.badge') || 'Our Work'}
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              {t('ourWork.heroTitle') || 'Businesses We Work With'}
            </h1>
            <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto">
              {t('ourWork.heroDesc') || 'Explore our portfolio of successful projects. Real results for real businesses.'}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Instagram — See more work on Instagram (moved to TOP per user request) */}
      <section className="py-8 md:py-12 px-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px]"
            style={{ background: 'radial-gradient(ellipse at center, rgba(236, 72, 153, 0.10) 0%, transparent 60%)' }}
          />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <motion.a
            href="https://www.instagram.com/pirone.media/reposts/"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="group relative block p-8 md:p-12 rounded-3xl bg-gradient-to-br from-pink-500/10 via-purple-500/5 to-transparent border border-pink-500/20 hover:border-pink-500/40 transition-all duration-500 overflow-hidden"
            data-testid="instagram-reposts-cta"
          >
            <div
              className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full pointer-events-none opacity-40 group-hover:opacity-70 transition-opacity duration-500"
              style={{ background: 'radial-gradient(circle, rgba(236, 72, 153, 0.4) 0%, transparent 70%)' }}
            />
            <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-gradient-to-br from-[#E4405F] via-[#8B5CF6] to-[#F59E0B] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform duration-500">
                <Instagram className="w-10 h-10 md:w-12 md:h-12 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/10 border border-pink-500/20 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" />
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pink-400">
                    {t('ourWork.instagramBadge', 'More on Instagram')}
                  </span>
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 group-hover:text-pink-400 transition-colors">
                  {t('ourWork.instagramTitle', 'See Even More of Our Work')}
                </h3>
                <p className="text-white/60 text-base md:text-lg leading-relaxed">
                  {t('ourWork.instagramDesc', 'Visit our Instagram to explore hundreds of reposts, reels and behind-the-scenes content from our clients.')}
                </p>
                <span className="inline-flex items-center gap-2 mt-4 text-white font-semibold text-sm group-hover:gap-3 transition-all">
                  @pirone.media
                  <ExternalLink size={16} />
                </span>
              </div>
            </div>
          </motion.a>
        </div>
      </section>

      {/* Videos Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-500/10 rounded-lg">
                <Video className="w-6 h-6 text-purple-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                {t('ourWork.videosTitle') || 'Videos'}
              </h2>
            </div>
            <p className="text-gray-400">
              {t('ourWork.videosDesc') || 'Watch our project showcases and behind-the-scenes content'}
            </p>
          </motion.div>

          {/* Category Filter Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-wrap items-center gap-2 mb-10"
            data-testid="category-filter"
          >
            {[
              { id: 'all', label: t('ourWork.filterAll', 'All') },
              { id: 'restaurant', label: t('ourWork.filterRestaurant', 'Restaurants') },
              { id: 'cafe', label: t('ourWork.filterCafe', 'Cafes') },
              { id: 'bakery', label: t('ourWork.filterBakery', 'Bakeries') },
              { id: 'retail', label: t('ourWork.filterRetail', 'Retail') }
            ].map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat.id
                    ? 'bg-white text-black shadow-lg'
                    : 'bg-white/[0.04] text-white/60 border border-white/10 hover:text-white hover:border-white/25'
                }`}
                data-testid={`filter-btn-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>

          {/* 3 Horizontal Videos */}
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {horizontalVideos.filter(v => activeCategory === 'all' || v.category === activeCategory).map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-gradient-to-br from-gray-900/80 to-gray-900/40 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-all duration-500"
                data-testid={`video-card-${video.id}`}
              >
                <div className="relative aspect-video overflow-hidden bg-gray-800">
                  <video
                    ref={el => (videoRefs.current[video.id] = el)}
                    src={video.src}
                    className="w-full h-full object-cover"
                    playsInline
                    preload="metadata"
                    onEnded={() => setPlayingVideo(null)}
                  />
                  <div
                    className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300 ${playingVideo === video.id ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}
                    onClick={() => handlePlayVideo(video.id)}
                  >
                    <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
                      {playingVideo === video.id ? (
                        <Pause className="w-8 h-8 text-white" />
                      ) : (
                        <Play className="w-8 h-8 text-white ml-1" />
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); openModal(video); }}
                    className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                    aria-label="Open fullscreen"
                    data-testid={`fullscreen-btn-${video.id}`}
                  >
                    <Maximize2 className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors">
                    {video.title}
                  </h3>
                  <p className="text-gray-400 text-sm">{video.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* 3 Vertical Videos (Reels) */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {verticalVideos.filter(v => activeCategory === 'all' || v.category === activeCategory).map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-gradient-to-br from-gray-900/80 to-gray-900/40 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-pink-500/30 transition-all duration-500"
                data-testid={`video-card-${video.id}`}
              >
                <div className="relative aspect-[9/16] overflow-hidden bg-gray-800">
                  <video
                    ref={el => (videoRefs.current[video.id] = el)}
                    src={video.src}
                    className="w-full h-full object-cover"
                    playsInline
                    preload="metadata"
                    onEnded={() => setPlayingVideo(null)}
                  />
                  <div
                    className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-300 ${playingVideo === video.id ? 'opacity-0 hover:opacity-100' : 'opacity-100'}`}
                    onClick={() => handlePlayVideo(video.id)}
                  >
                    <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors">
                      {playingVideo === video.id ? (
                        <Pause className="w-6 h-6 text-white" />
                      ) : (
                        <Play className="w-6 h-6 text-white ml-1" />
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); openModal(video); }}
                    className="absolute top-3 right-3 p-2 bg-black/60 hover:bg-black/80 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                    aria-label="Open fullscreen"
                    data-testid={`fullscreen-btn-${video.id}`}
                  >
                    <Maximize2 className="w-4 h-4 text-white" />
                  </button>
                </div>
                <div className="p-3">
                  <h3 className="text-sm font-semibold text-white group-hover:text-pink-400 transition-colors truncate">
                    {video.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Websites Section */}
      <section className="py-16 px-4 relative overflow-hidden">
        {/* Background Glow Effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-[600px] h-[600px]"
            style={{ background: 'radial-gradient(circle at center, rgba(56, 189, 248, 0.06) 0%, transparent 60%)' }}
          />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px]"
            style={{ background: 'radial-gradient(circle at center, rgba(139, 92, 246, 0.05) 0%, transparent 60%)' }}
          />
        </div>

        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-cyan-500/10 rounded-lg">
                <Globe className="w-6 h-6 text-cyan-400" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white">
                {t('ourWork.websitesTitle') || 'Websites'}
              </h2>
            </div>
            <p className="text-gray-400">
              {t('ourWork.websitesDesc') || 'Click to explore our client websites'}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {websites.map((website, index) => (
              <motion.div
                key={website.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-gradient-to-br from-gray-900/80 to-gray-900/40 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-500/30 transition-all duration-500"
              >
                {/* Website Preview */}
                <div className="relative aspect-video overflow-hidden bg-gray-800">
                  {website.url ? (
                    <iframe
                      src={website.url}
                      title={website.name}
                      className="w-full h-full transform scale-[0.5] origin-top-left"
                      style={{ width: '200%', height: '200%' }}
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                      <div className="text-center p-6">
                        <Globe className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <p className="text-gray-500 text-sm">
                          {t('ourWork.websitePlaceholder') || 'Website preview will appear here'}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    {website.url && (
                      <a
                        href={website.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-5 h-5" />
                        {t('ourWork.visitSite') || 'Visit Website'}
                      </a>
                    )}
                  </div>
                </div>

                {/* Website Info */}
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                    {website.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4">
                    {website.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {website.tags.map((tag, i) => (
                      <span
                        key={i}
                        className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-xs text-gray-300"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border border-white/10 rounded-3xl p-8 md:p-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('ourWork.ctaTitle') || 'Ready to Join Our Success Stories?'}
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              {t('ourWork.ctaDesc') || 'Let us help you build something amazing. Your business could be our next showcase.'}
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-cyan-400 hover:from-cyan-400 hover:to-cyan-300 text-black font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40"
            >
              {t('ourWork.ctaButton') || 'Start Your Project'}
            </a>
          </motion.div>
        </div>
      </section>

      {/* Video Modal - Pro player with native controls (seek, volume, fullscreen) */}
      {selectedVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4"
          onClick={closeModal}
          data-testid="video-modal"
        >
          <div
            className={`relative ${selectedVideo.id >= 7 ? 'max-w-sm w-full' : 'w-full max-w-5xl'}`}
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeModal}
              className="absolute -top-12 right-0 p-2 text-white hover:text-cyan-400 transition-colors z-10"
              aria-label="Close"
              data-testid="modal-close-btn"
            >
              <X className="w-8 h-8" />
            </button>

            {/* Mute toggle (extra control on top of native ones) */}
            <button
              onClick={toggleModalMute}
              className="absolute -top-12 right-14 p-2 text-white hover:text-cyan-400 transition-colors z-10"
              aria-label={modalMuted ? 'Unmute' : 'Mute'}
              data-testid="modal-mute-btn"
            >
              {modalMuted ? <VolumeX className="w-7 h-7" /> : <Volume2 className="w-7 h-7" />}
            </button>

            <video
              ref={modalVideoRef}
              key={selectedVideo.id}
              src={selectedVideo.src}
              controls
              controlsList="nodownload"
              autoPlay
              playsInline
              className={`rounded-xl bg-black ${selectedVideo.id >= 7 ? 'max-h-[80vh] w-auto mx-auto' : 'w-full'}`}
            />
            <div className="mt-4 text-center">
              <h3 className="text-xl font-semibold text-white">{selectedVideo.title}</h3>
              <p className="text-gray-400 text-sm">{selectedVideo.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OurWorkPage;
